/**
 * publishedUnit (#2944) — which unit a tile's facts were actually published under.
 *
 * ── The bug this exists to make unrepresentable ───────────────────────────────
 *
 * A price fact is written with its unit on it: all three writers stamp
 * ``fact_value.unit`` and have since #2394. The catalog ALSO declares a unit per
 * item, and until #2863 nobody had ever moved one, so the two were
 * interchangeable and the render path read the catalog — it is right there in
 * ``item.unit``, no data plumbing required.
 *
 * #2863 moved four PSM tiles from ``€/kg`` to ``€/l``, and the two stopped being
 * the same statement. Facts already on disk are not retired by a catalog edit
 * (nor should they be — see below), so a ``380,00 €/kg`` observation kept its
 * value and acquired a new label: ``380,00 €/l``. Same number, silently
 * different meaning, no error anywhere.
 *
 * The rule this module implements is therefore: **the unit travels on the fact.**
 * ``item.unit`` is a default — for new writes, and for an empty tile where there
 * is nothing to mislabel. It is never a truth about facts already written.
 *
 * ── Why "mixed" is a refusal and not a tie-break ──────────────────────────────
 *
 * Once units live on facts, one tile can hold two of them: exactly what a flip
 * produces while old and new facts coexist. A tile headline is one number and a
 * chart Y-axis is one axis, and neither can honestly carry two units. Picking a
 * winner — most recent, most rows, the catalog's — is how the wrong one wins
 * silently, which is the original bug wearing a different hat. So the label is
 * SUPPRESSED and the condition reported (``mixed``), in the same fail-loud
 * posture as the surrounding ``degraded``/``truncated`` flags.
 *
 * ── What this deliberately does NOT do ────────────────────────────────────────
 *
 * It does not retire, hide or rewrite anything. A fact that stops being emitted
 * by a later run is **not** thereby wrong, and a fact published under a unit the
 * catalog no longer uses is not wrong either — it is a true record of a past
 * trade. Both stay visible, correctly labelled. See
 * ``docs/operations/MYFARM24_PRICE_LIST_STAGE.md`` §4.
 */
import { PRICE_UNITS, type PriceUnit } from './catalog';
import type { PriceSeries } from './types';

/** What can honestly be said about the unit behind one tile's data. */
export interface PublishedUnit {
  /**
   * The single unit every live fact behind the tile was published under, or
   * ``null`` when no single unit can be stated — either because they disagree
   * (see {@link mixed}) or because none of them says.
   */
  unit: PriceUnit | null;
  /**
   * The live facts carry MORE THAN ONE unit. The label is suppressed rather than
   * resolved; a consumer should mark the tile rather than print a winner.
   */
  mixed: boolean;
}

const isPriceUnit = (value: unknown): value is PriceUnit =>
  typeof value === 'string' && (PRICE_UNITS as readonly string[]).includes(value);

/**
 * Resolve the unit for one tile's population.
 *
 * ``series`` is the population the tile actually speaks for — on a booked tile
 * the booked series alone, per #2777's "booked always wins". A curated reference
 * price in another unit must not blank a traded tile's headline; it is not one of
 * the facts that tile is reporting.
 *
 * ``indexUnits`` are the units the ``/index`` read attributed to this item.
 * They are the LIVE carrier: the catalog-wide ``/cells`` read cannot state a
 * unit (its group key is at UDB's ceiling), so in production the series arrive
 * unstamped and the index answers instead. Both sources are unioned rather than
 * ranked — they aggregate the same facts, so a disagreement between them is
 * itself the mixed-unit signal and must not be resolved by preferring one.
 *
 * Only series WITH POINTS vote. An empty series is not a fact the tile renders,
 * so a stale unit stamp on one cannot mislabel anything.
 */
export function resolvePublishedUnit(
  series: readonly PriceSeries[],
  indexUnits: readonly (string | null | undefined)[] = [],
): PublishedUnit {
  const found = new Set<PriceUnit>();
  for (const s of series) {
    if (s.points.length === 0) continue;
    if (isPriceUnit(s.unit)) found.add(s.unit);
  }
  for (const candidate of indexUnits) {
    if (isPriceUnit(candidate)) found.add(candidate);
  }
  if (found.size === 1) return { unit: [...found][0], mixed: false };
  // size 0 → nothing states a unit. Say nothing: on a tile that HAS data,
  // falling back to ``item.unit`` is the very substitution this module exists to
  // prevent. A missing unit is visible and recoverable; a confident wrong one is
  // neither.
  return { unit: null, mixed: found.size > 1 };
}

/**
 * The QUANTITY unit behind a price unit — the denominator of ``€/kg``, ``ct/l``,
 * ``€/Stk`` — or ``null`` when none can be stated.
 *
 * #2944, adjacent scope: the day panel renders a traded quantity derived as
 * ``provenance.amount / value`` (``aggregate.catalogUnitKg``). That quotient is
 * money over a price, so it is denominated in whatever the price's denominator
 * is: kilograms on a ``€/kg`` tile, LITRES on a ``€/l`` one. It was formatted
 * with a hardcoded ``kg``/``t``, so a per-litre tile reported litres as
 * kilograms — the same mislabel as the headline, one column over. Fixing the
 * price label while leaving this would have been the more confusing half-state.
 */
export function quantityUnit(unit: string | null | undefined): string | null {
  if (!unit) return null;
  const denominator = unit.split('/')[1];
  return denominator ? denominator : null;
}

/**
 * A traded quantity for the reader — kg below a tonne, t at or above it.
 *
 * Relocated here from `PriceChart`'s private `weightFmt` by HOF-310, unchanged,
 * because the volume pane and the day panel now print the SAME quantity in two
 * places and two spellings of this rule is how one of them ends up reporting
 * litres as kilograms all over again.
 *
 * The switch is on the ACTUAL kilos, not on a display preference: farm-scale
 * rows span four orders of magnitude in one list (a 6,5 kg Hofladen position
 * beside a 12 t Frutania delivery), and rendering both in one unit makes the
 * list either unreadable ("12.000 kg") or lossy ("0,0065 t").
 *
 * #2944 — it is not always kilograms. The quantity is `amount / value`, money
 * over a price, so it is denominated in whatever the PRICE's denominator is: a
 * `€/l` tile yields LITRES. `qtyUnit` is that denominator (see
 * {@link quantityUnit}), and the kg→t rescale fires only for kilograms, since
 * the thousand-fold step is a fact about the metric mass scale. A null
 * `qtyUnit` prints the bare number rather than asserting one.
 */
export function formatQuantity(qty: number, qtyUnit: string | null): string {
  const fmt = (v: number) => new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 }).format(v);
  if (qtyUnit === 'kg' && qty >= 1000) return `${fmt(qty / 1000)} t`;
  return qtyUnit ? `${fmt(qty)} ${qtyUnit}` : fmt(qty);
}

/**
 * What `amount / value` must be multiplied by to be a quantity — or ``null``
 * when no factor can be stated.
 *
 * HOF-340. {@link quantityUnit} answers the DENOMINATOR question; this is the
 * NUMERATOR one, and #2944 is the reason they are now written next to each
 * other. `catalogUnitKg` divides `provenance.amount` — the line total in euros —
 * by the fact's price in the unit the fact declares. That quotient is a quantity
 * only where the price's numerator is euros too. `ct/l` is the one member of
 * `PRICE_UNITS` whose numerator is not: €450,00 at 45 ct/l yields `10`, which is
 * HECTOLITRES, and `quantityUnit('ct/l')` labels it `l` — 10 l printed for the
 * 1.000 litres delivered, understated by exactly 100.
 *
 * `config/metrics/price_observation.yaml:146-149` states the same identity from
 * the backend side, in the same words (*"hectolitres for ct/l"*), and adds why
 * it is nonetheless safe THERE: the factor cancels inside a unit group, and that
 * file is only ever talking about a weighting. Both halves are correct in
 * isolation; the defect was the step between them, where a display takes the
 * quotient out of the weighting and nothing cancels.
 *
 * ``null`` rather than 1 for a unit that states nothing, and rather than 1 for a
 * numerator this does not know. An unresolved tile is an unknown SCALE and not
 * merely an unknown label — it may be the `ct/l` tile — which is `volumePane`'s
 * `VOLUME_UNKNOWN_UNIT_TITLE` reasoning applied to the same quantity: a missing
 * number degrades gracefully, a wrong magnitude does not.
 */
export function quantityScale(unit: string | null | undefined): number | null {
  if (!unit) return null;
  const numerator = unit.split('/')[0];
  if (numerator === '€') return 1;
  if (numerator === 'ct') return 100;
  return null;
}

/**
 * One traded quantity, rendered — or ``null`` where it may not be.
 *
 * HOF-340 pairs {@link quantityScale} with {@link quantityUnit} in one function
 * because taking the label without the scale IS this defect: #2944 re-labelled
 * the day panel's column (`kg` → the price's denominator) and left the number
 * unscaled, so a `ct/l` tile went from confidently wrong in its unit to
 * confidently wrong in its magnitude. A caller that cannot reach one half
 * without the other cannot repeat that.
 *
 * `qty` is `catalogUnitKg`'s raw quotient, in the basis of the tile's own
 * catalog unit. The identity itself is deliberately left alone: it is shared
 * with `bucketPrice`'s weight path and with the drill-down's sort key, both of
 * which are scale-INVARIANT (`Σ(value × kg) / Σ kg`, and a uniform factor is
 * order-preserving), so moving the factor upstream would be a change to the
 * PRICE path rather than to the display.
 */
export function formatTradedQuantity(qty: number, unit: string | null | undefined): string | null {
  const scale = quantityScale(unit);
  if (scale == null) return null;
  return formatQuantity(qty * scale, quantityUnit(unit));
}
